import React, { useState } from 'react'
import { IconButton, InputAdornment, InputBase, type SxProps, styled } from '@mui/material'

import {
  borderRadius,
  lightTokens,
  maxWidth,
  spacing,
  transitions,
  typography as typographyTokens,
} from '~tokens/internal'

import Icon from './Icon'

const SearchBase = styled(InputBase)(({ theme }) => ({
  width: '100%',
  height: '40px',
  borderRadius: borderRadius.sm,
  backgroundColor: lightTokens.background.background3,
  transition: `background-color ${transitions.duration.normal} ${transitions.easing.easeInOut}`,

  '&:hover': {
    backgroundColor: lightTokens.background.background2,
  },

  '&:focus-within': {
    backgroundColor: lightTokens.background.background3,
  },

  '& .MuiInputBase-input': {
    fontSize: typographyTokens.fontSize.sm,
    fontFamily: typographyTokens.fontFamily.body,
    letterSpacing: typographyTokens.letterSpacing.wide,
    lineHeight: '24px',
    padding: `${spacing.xxs} ${spacing.compact}`,
    color: theme.palette.text.primary,
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
    width: '100%',
    '&::placeholder': {
      color: lightTokens.text.secondary,
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  },

  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
    maxHeight: 'none',
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: spacing.compact,
    marginRight: spacing.none,
  },
  '& .MuiInputAdornment-positionEnd': {
    marginRight: spacing.micro,
  },
  '&.MuiInputBase-adornedStart .MuiInputBase-input': {
    paddingLeft: spacing.xxs,
    flex: 1,
    minWidth: 0,
  },
  '&.MuiInputBase-adornedEnd .MuiInputBase-input': {
    paddingRight: spacing.micro,
    flex: 1,
    minWidth: 0,
  },

  '& .search__label-anchor': {
    position: 'relative',
    width: 0,
  },

  '& .search__label': {
    position: 'absolute',
    left: spacing.xxs,
    top: '50%',
    transform: 'translateY(-50%)',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    fontSize: typographyTokens.fontSize.sm,
    fontFamily: typographyTokens.fontFamily.body,
    letterSpacing: typographyTokens.letterSpacing.wide,
    lineHeight: '24px',
    color: lightTokens.text.secondary,
  },

  '& .search__label-suffix': {
    display: 'inline-block',
    whiteSpace: 'pre',
    clipPath: 'inset(0 0 0 0)',
    opacity: 1,
    transition: `clip-path ${transitions.duration.normal} ${transitions.easing.easeOut}, opacity ${transitions.duration.normal} ${transitions.easing.easeOut}`,
  },

  '&.search--collapsed': {
    backgroundColor: 'transparent',
    cursor: 'pointer',

    '& .MuiInputBase-input': {
      cursor: 'pointer',
    },

    '& .search__label-suffix': {
      clipPath: 'inset(0 100% 0 0)',
      opacity: 0,
    },
  },

  '&.search--collapsed:hover': {
    backgroundColor: lightTokens.background.background2,
  },
}))

const clearButtonStyles = {
  padding: spacing.micro,
  color: lightTokens.text.secondary,
  '&:hover': {
    color: lightTokens.text.primary,
    backgroundColor: 'transparent',
  },
} as const

export type SearchWidth = 'sm' | 'md' | 'full'

const searchWidthMap: Record<SearchWidth, string> = {
  sm: maxWidth.searchSm,
  md: maxWidth.searchMd,
  full: maxWidth.full,
}

export interface SearchProps {
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onClear?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  inputRef?: React.Ref<HTMLInputElement>
  /** Constrains the max-width of the search bar using design tokens. Defaults to 'full'. */
  width?: SearchWidth
  collapsible?: boolean
  collapsedLabel?: string
  sx?: SxProps
}

const Search = React.forwardRef<HTMLDivElement, SearchProps>(
  (
    {
      value,
      onChange,
      onClear,
      placeholder = 'Search',
      disabled,
      autoFocus,
      inputRef,
      width,
      collapsible = false,
      collapsedLabel = 'Search',
      sx,
      ...rest
    },
    ref,
  ) => {
    const [focused, setFocused] = useState(false)
    const widthSx = width ? { maxWidth: searchWidthMap[width] } : {}
    const collapsed = collapsible && !focused && !value
    const labelSuffix = placeholder.startsWith(collapsedLabel)
      ? placeholder.slice(collapsedLabel.length)
      : ''
    const labelText = labelSuffix || collapsed ? collapsedLabel : placeholder

    const handleClear = (): void => {
      if (onClear) {
        onClear()
      }
    }

    return (
      <SearchBase
        ref={ref}
        value={value}
        onChange={onChange}
        placeholder={collapsible ? undefined : placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        inputRef={inputRef}
        inputProps={collapsible ? { 'aria-label': placeholder } : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={collapsed ? 'search--collapsed' : undefined}
        startAdornment={
          <InputAdornment position='start'>
            <Icon name='search' fontSize='small' />
            {collapsible && !value && (
              <span className='search__label-anchor'>
                <span className='search__label'>
                  {labelText}
                  {labelSuffix && <span className='search__label-suffix'>{labelSuffix}</span>}
                </span>
              </span>
            )}
          </InputAdornment>
        }
        endAdornment={
          value && onClear ? (
            <InputAdornment position='end'>
              <IconButton
                onClick={handleClear}
                size='small'
                aria-label='Clear search'
                sx={clearButtonStyles}
              >
                <Icon name='close' fontSize='small' />
              </IconButton>
            </InputAdornment>
          ) : null
        }
        sx={[widthSx, ...(Array.isArray(sx) ? sx : [sx])]}
        {...rest}
      />
    )
  },
)

export { SearchBase }
export default Search
